import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Layout from "../components/Layout";
import SEO, { ORGANIZATION_JSON_LD } from "../components/SEO";
import { api, fmtMoney, convert } from "../lib/api";
import { useCurrency } from "../contexts/CurrencyContext";
import InstallAppButton from "../components/InstallAppButton";

const HERO_BG = "https://images.pexels.com/photos/28410257/pexels-photo-28410257.jpeg";

function Stat({ label, value, sub }) {
  return (
    <div className="border-l border-line pl-6 py-2">
      <div className="label-tag">{label}</div>
      <div className="mono-num text-2xl mt-1 text-ink">{value}</div>
      {sub && <div className="text-xs text-ink2 mt-1">{sub}</div>}
    </div>
  );
}

export default function Landing() {
  const { t } = useTranslation();
  const { currency } = useCurrency();
  const [prices, setPrices] = useState({});
  const [ucs, setUcs] = useState(null);
  const [projects, setProjects] = useState([]);

  useEffect(() => {
    api.get("/market/prices").then((r) => setPrices(r.data.prices || {})).catch(() => {});
    api.get("/market/ucs-index").then((r) => setUcs(r.data)).catch(() => {});
    api.get("/projects").then((r) => setProjects((r.data || []).slice(0, 3))).catch(() => {});
  }, []);

  const bct = prices["toucan-protocol-base-carbon-tonne"];
  const mco2 = prices["moss-carbon-credit"];
  const klima = prices["klima-dao"];

  return (
    <Layout>
      <SEO
        title="Verified UCS Projects Exchange"
        description="Trade UCS (Unidade de Crédito de Sustentabilidade) — a regulated Brazilian carbon credit asset. Multi-currency settlement in USD, EUR, BRL, GBP and SAR via Stripe, SWIFT and USDT."
        path="/"
        jsonLd={ORGANIZATION_JSON_LD}
      />
      {/* HERO */}
      <section className="relative">
        <div className="absolute inset-0">
          <img src={HERO_BG} alt="forest" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-bg via-bg/70 to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-bg/40" />
        </div>

        <div className="relative max-w-7xl mx-auto px-6 lg:px-10 pt-24 pb-32">
          <div className="max-w-3xl">
            <div className="label-tag mb-6" data-testid="hero-tag">{t("hero.tag")}</div>
            <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl text-ink leading-[1.05] tracking-tight">
              {t("hero.title_a")} <em className="font-medium">{t("hero.title_em")}</em>{t("hero.title_b")}
            </h1>
            <p className="mt-8 text-lg text-ink2 max-w-xl leading-relaxed font-sans">
              {t("hero.sub")}
            </p>
            <div className="mt-10 flex flex-wrap gap-3" data-testid="hero-ctas">
              <Link to="/marketplace" className="btn-primary" data-testid="cta-explore">{t("hero.cta_explore")}</Link>
              <Link to="/demo" className="btn-outline" data-testid="cta-demo">
                <span className="inline-block w-1.5 h-1.5 rounded-full bg-leaf animate-pulse mr-2 align-middle"></span>
                {t("demo.cta")}
              </Link>
              <Link to="/register" className="btn-outline" data-testid="cta-open">{t("hero.cta_open")}</Link>
              <InstallAppButton variant="ghost" />
            </div>
          </div>

          {/* UCS INDEX hero card */}
          <div className="mt-16 border border-ink bg-surface p-6 lg:p-8 max-w-3xl" data-testid="ucs-index-card">
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="label-tag">{t("ucs.card_tag")}</div>
                <div className="font-serif text-2xl mt-1">{t("ucs.card_title")}</div>
              </div>
              <div className={`mono-num text-sm ${ucs && ucs.change_pct_24h >= 0 ? "text-leaf" : "text-danger"}`}>
                {ucs ? `${ucs.change_pct_24h >= 0 ? "+" : ""}${ucs.change_pct_24h}% 24h` : "—"}
              </div>
            </div>
            <div className="grid grid-cols-3 gap-px bg-line border border-line">
              {["BRL", "USD", "EUR"].map((c) => (
                <div key={c} className="bg-surface p-4">
                  <div className="label-tag">{c}</div>
                  <div className="mono-num text-2xl mt-1">
                    {ucs ? ucs.spot[c]?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : "—"}
                  </div>
                </div>
              ))}
            </div>
            <div className="text-[11px] font-mono text-ink2 mt-3 tracking-wider">
              {t("ucs.footnote")}
            </div>
          </div>

          {/* Live ticker */}
          <div className="mt-20 grid grid-cols-2 md:grid-cols-4 gap-y-6">
            <Stat label="BCT · Toucan" value={bct ? `$${bct.usd?.toFixed(2)}` : "—"}
                  sub={bct?.usd_24h_change ? `${bct.usd_24h_change.toFixed(2)}% 24h` : ""} />
            <Stat label="MCO2 · Moss" value={mco2 ? `$${mco2.usd?.toFixed(2)}` : "—"}
                  sub={mco2?.usd_24h_change ? `${mco2.usd_24h_change.toFixed(2)}% 24h` : ""} />
            <Stat label="KLIMA · Klima DAO" value={klima ? `$${klima.usd?.toFixed(2)}` : "—"}
                  sub={klima?.usd_24h_change ? `${klima.usd_24h_change.toFixed(2)}% 24h` : ""} />
            <Stat label="Display currency" value={currency} sub="Switch in header" />
          </div>
        </div>
      </section>

      {/* HOW · BMV Global 5-step UCS lifecycle */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-24">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12">
          <div className="md:col-span-4">
            <div className="label-tag mb-4">{t("method.tag")}</div>
            <h2 className="font-serif text-4xl lg:text-5xl tracking-tight leading-tight">
              {t("method.title")}
            </h2>
            <p className="mt-5 text-ink2 leading-relaxed">
              {t("method.desc")}
            </p>
          </div>
          <div className="md:col-span-8 grid grid-cols-1 sm:grid-cols-2 gap-px bg-line border border-line">
            {[
              { n: "01", t: t("method.s1"), d: t("method.s1d") },
              { n: "02", t: t("method.s2"), d: t("method.s2d") },
              { n: "03", t: t("method.s3"), d: t("method.s3d") },
              { n: "04", t: t("method.s4"), d: t("method.s4d") },
              { n: "05", t: t("method.s5"), d: t("method.s5d"), colspan: "sm:col-span-2" },
            ].map((s) => (
              <div key={s.n} className={`bg-surface p-6 ${s.colspan || ""}`}>
                <div className="font-mono text-xs text-ink2">{s.n}</div>
                <div className="font-serif text-2xl mt-2">{s.t}</div>
                <div className="mt-3 text-sm text-ink2 leading-relaxed">{s.d}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHO IS IT FOR */}
      <section className="border-t border-line bg-muted/30">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20">
          <div className="label-tag mb-3">{t("who.tag")}</div>
          <h2 className="font-serif text-4xl lg:text-5xl tracking-tight">{t("who.title")}</h2>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-4 gap-px bg-line border border-line">
            {[
              [t("who.labels.companies"), t("who.companies")],
              [t("who.labels.owners"), t("who.owners")],
              [t("who.labels.investors"), t("who.investors")],
              [t("who.labels.governments"), t("who.governments")],
            ].map(([k, v]) => (
              <div key={k} className="bg-surface p-6">
                <div className="label-tag">{k}</div>
                <div className="font-serif text-xl mt-2 leading-tight">{k}</div>
                <div className="text-sm text-ink2 mt-3 leading-relaxed">{v}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TRUST RIBBON */}
      <section className="border-t border-line">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-14">
          <div className="label-tag mb-6">{t("trust.label")}</div>
          <div className="flex flex-wrap items-center gap-x-10 gap-y-4 font-serif text-2xl text-ink2">
            {["SGS", "Banco Central", "B3", "United Nations", "Fenasbac", "ERM", "BNDES", "Banco da Amazônia", "SEBRAE"].map((p) => (
              <span key={p} className="hover:text-ink transition-colors">{p}</span>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURED PROJECTS */}
      <section className="bg-muted/40 border-y border-line">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24">
          <div className="flex items-end justify-between mb-10">
            <div>
              <div className="label-tag mb-3">02 · Featured projects</div>
              <h2 className="font-serif text-4xl lg:text-5xl tracking-tight">From the marketplace.</h2>
            </div>
            <Link to="/marketplace" className="btn-outline !py-2 !px-5 !text-xs">All listings →</Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-testid="featured-projects">
            {projects.map((p) => (
              <Link to={`/project/${p.id}`} key={p.id} className="card-tech group overflow-hidden" data-testid={`featured-${p.id}`}>
                <div className="aspect-[4/3] overflow-hidden bg-muted">
                  <img src={p.image_url} alt={p.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                </div>
                <div className="p-5">
                  <div className="label-tag">{p.type} · {p.region}</div>
                  <div className="font-serif text-xl mt-2 leading-tight">{p.name}</div>
                  <div className="mt-4 flex items-center justify-between">
                    <div className="mono-num text-lg text-ink">
                      {fmtMoney(convert(p.price_usd_per_ton, currency), currency)}
                      <span className="text-xs text-ink2 ml-1">/ tCO₂</span>
                    </div>
                    <div className="text-xs font-mono text-ink2">{p.certifier}</div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* MOBILE APP CTA */}
      <section className="border-t border-line bg-ink text-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20 grid grid-cols-1 md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-7">
            <div className="label-tag !text-lime mb-4">GREENBELT MOBILE</div>
            <h2 className="font-serif text-4xl lg:text-5xl tracking-tight leading-tight">
              Trade UCS on the go.
            </h2>
            <p className="mt-5 text-white/70 max-w-xl">
              Available as an installable web app today on iPhone &amp; Android. Native iOS and Android apps coming soon to the App Store and Google Play.
            </p>
            <div className="mt-8 flex flex-wrap gap-3" data-testid="app-store-ctas">
              <a href={process.env.REACT_APP_IOS_APP_URL || "#"}
                 target={process.env.REACT_APP_IOS_APP_URL ? "_blank" : "_self"}
                 rel="noreferrer"
                 className={`inline-flex items-center gap-3 border ${process.env.REACT_APP_IOS_APP_URL ? "border-lime text-lime hover:bg-lime hover:text-ink" : "border-white/30 hover:border-lime hover:text-lime"} px-5 py-3 transition-colors`}
                 data-testid="ios-cta">
                <i className="fab fa-apple text-2xl"></i>
                <span className="text-left leading-tight">
                  <span className="block text-[10px] tracking-widest text-white/60">{process.env.REACT_APP_IOS_APP_URL ? "DOWNLOAD ON THE" : "COMING SOON ·"}</span>
                  <span className="block font-serif text-lg">App Store</span>
                </span>
              </a>
              <a href={process.env.REACT_APP_ANDROID_APP_URL || "#"}
                 target={process.env.REACT_APP_ANDROID_APP_URL ? "_blank" : "_self"}
                 rel="noreferrer"
                 className={`inline-flex items-center gap-3 border ${process.env.REACT_APP_ANDROID_APP_URL ? "border-lime text-lime hover:bg-lime hover:text-ink" : "border-white/30 hover:border-lime hover:text-lime"} px-5 py-3 transition-colors`}
                 data-testid="android-cta">
                <i className="fab fa-google-play text-xl"></i>
                <span className="text-left leading-tight">
                  <span className="block text-[10px] tracking-widest text-white/60">{process.env.REACT_APP_ANDROID_APP_URL ? "GET IT ON" : "COMING SOON ·"}</span>
                  <span className="block font-serif text-lg">Google Play</span>
                </span>
              </a>
              <button onClick={() => window.alert('On iPhone: tap Share → Add to Home Screen.\nOn Android: tap menu → Install app.')} className="inline-flex items-center gap-3 border border-lime text-lime hover:bg-lime hover:text-ink px-5 py-3 transition-colors" data-testid="pwa-install">
                <i className="fas fa-mobile-screen text-xl"></i>
                <span className="font-mono text-xs tracking-widest">INSTALL NOW (PWA)</span>
              </button>
            </div>
          </div>
          <div className="md:col-span-5 flex justify-center">
            <div className="border border-white/20 px-6 py-10 text-center bg-white/5 max-w-xs">
              <div className="font-mono text-[10px] tracking-[0.3em] text-lime">QR · SCAN TO INSTALL</div>
              <img alt="QR" className="mx-auto my-5 bg-white p-2 w-40 h-40 object-contain"
                src="https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=https%3A%2F%2Fcredit-marketplace-11.preview.emergentagent.com" />
              <div className="text-xs text-white/70">Opens GreenBelt — then tap "Add to Home Screen".</div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-24">
        <div className="border border-ink p-10 lg:p-16">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-10 items-center">
            <div>
              <div className="label-tag mb-4">{t("hero.cta_open")}</div>
              <h3 className="font-serif text-4xl tracking-tight leading-tight">
                {t("cta.title")}
              </h3>
            </div>
            <div className="flex md:justify-end">
              <Link to="/register" className="btn-primary" data-testid="cta-bottom">{t("cta.btn")}</Link>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
}
